import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { fetchAllJourneyPaths } from '../../lib/contentClient';
import { courseRead, filterSavedCourses, type CourseFilter } from '../../lib/exploreModel';
import { lessonProgressService } from '../../lib/lessonProgressService';
import { readDeck } from '../../lib/reviewDeck';
import { savedCourseSlugs } from '../../lib/offlineCourse';
import { levelFromXp, sortSavedCards, totalXp } from '../../lib/studyStats';
import { readStudyStats } from '../../lib/studyStatsStore';

export default function MePage() {
  const { user } = useAuth();
  const [cardSort, setCardSort] = useState<'title' | 'date'>('date');
  const [courseFilter, setCourseFilter] = useState<CourseFilter>('all');
  const stats = readStudyStats();
  const cards = sortSavedCards(readDeck(), cardSort);
  const saved = savedCourseSlugs();
  const pathsQuery = useQuery({ queryKey: ['journey-paths'], queryFn: () => fetchAllJourneyPaths() });
  const progressQuery = useQuery({
    queryKey: ['lesson-progress', user?.id || 'device'],
    queryFn: () => lessonProgressService.getCompletedLessons(),
  });
  const completed = useMemo(() => [...(progressQuery.data || lessonProgressService.getCompletedLessonsFromLocal())], [progressQuery.data]);
  const xp = totalXp(stats, completed);
  const level = levelFromXp(xp);
  const name = user?.user_metadata?.full_name || user?.email || 'On this device';
  const pathRows = (pathsQuery.data || []).map((path) => ({
    slug: path.slug,
    title: path.primaryLanguage === 'en' ? path.titleEn || path.title : path.title,
    lessonIds: (path.modules || []).flatMap((module) => (module.lessons || []).map((lesson) => lesson._id)),
  }));
  const visibleCourses = filterSavedCourses(saved, pathRows, completed, courseFilter);

  return (
    <div className="font-myanmar mx-auto w-full max-w-lg min-w-0 overflow-x-hidden px-4 py-6" data-testid="me-page">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-gray-900">Me</h1>
          <p className="lesson-subtext text-sm text-gray-600">ကျွန်ုပ်</p>
          <p className="mt-2 break-words text-base font-semibold" data-testid="me-profile">{name}</p>
        </div>
        <Link to="/settings" className="min-h-[44px] shrink-0 rounded-2xl bg-teal-700 px-4 py-2 text-sm font-semibold text-white" data-testid="me-settings">
          Settings
        </Link>
      </div>

      <dl className="mt-6 grid grid-cols-2 gap-3">
        <Stat label="Level" value={String(level)} testId="me-level" />
        <Stat label="Longest streak" value={String(stats.longest)} testId="me-longest" />
        <Stat label="Lessons completed" value={String(completed.length)} testId="me-lessons" />
        <Stat label="XP" value={String(xp)} testId="me-xp" />
      </dl>
      <p className="mt-2 text-xs text-gray-600">Level goes up every 50 XP.</p>

      <section className="mt-8" data-testid="saved-cards">
        <h2 className="text-lg font-semibold">Saved Cards</h2>
        <div className="mt-2 flex gap-2">
          <button type="button" className="min-h-[44px] rounded-full bg-white px-4 text-sm font-semibold" data-testid="sort-title" aria-pressed={cardSort === 'title'} onClick={() => setCardSort('title')}>
            Title
          </button>
          <button type="button" className="min-h-[44px] rounded-full bg-white px-4 text-sm font-semibold" data-testid="sort-date" aria-pressed={cardSort === 'date'} onClick={() => setCardSort('date')}>
            Date
          </button>
        </div>
        {cards.length === 0 ? <p className="mt-3 text-sm text-gray-600">No saved cards yet.</p> : null}
        <ul className="mt-3 space-y-2">
          {cards.map((card) => (
            <li key={card.id} className="rounded-2xl bg-white px-4 py-3" data-testid="saved-card">
              <p className="break-words font-semibold">{card.title}</p>
              <p className="text-xs text-gray-500">{card.savedAt ? card.savedAt.slice(0, 10) : 'No date'}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8" data-testid="saved-courses">
        <h2 className="text-lg font-semibold">Saved Courses</h2>
        <div className="mt-2 flex flex-wrap gap-2">
          {([
            ['all', 'All'],
            ['to-read', 'To Read'],
            ['complete', 'Complete'],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`min-h-[44px] rounded-full px-4 text-sm font-semibold ${courseFilter === id ? 'bg-teal-700 text-white' : 'bg-white text-gray-900'}`}
              data-testid={`course-filter-${id}`}
              aria-pressed={courseFilter === id}
              onClick={() => setCourseFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>
        {visibleCourses.length === 0 ? <p className="mt-3 text-sm text-gray-600">No saved courses in this list.</p> : null}
        <ul className="mt-3 space-y-2">
          {visibleCourses.map((slug) => {
            const path = pathRows.find((item) => item.slug === slug);
            const read = courseRead(path ? path.lessonIds : null, completed);
            return (
              <li key={slug} data-testid="saved-course" data-read={read}>
                <Link to={`/courses/${slug}`} className="block break-words rounded-2xl bg-white px-4 py-3">
                  <span className="font-semibold">{path?.title || slug}</span>
                  <span className="mt-1 block text-xs text-gray-500">
                    {read === 'complete' ? 'Complete' : read === 'unknown' ? 'Saved' : 'To read'}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

function Stat({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="rounded-2xl bg-white px-4 py-3">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-2xl font-semibold text-gray-900" data-testid={testId}>{value}</dd>
    </div>
  );
}
